import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { AccountType, IntakeFormType, IntakeEvictionType, Permission } from '@ee/common/enums';
import {
  CaseSettings,
  CustomField,
  Eviction,
  IntakeEviction,
  IntakeFieldStyle,
  IntakeForm,
  IntakeOption,
  LayoutRule,
  Organization,
  User,
  Workflow
} from '@ee/common/models';
import { ClientService, CustomFieldService, IntakeEvictionService, IntakeFormService } from '@ee/common/services';
import { cloneNameFor, PlaintiffOptions, resolvePlaintiffValue, suggestedPlaintiffOptions } from '@ee/common/utils';
import { FileStillUploadingError, FileUploadFailedError } from '@ee/common/custom-field';
import { ToCreateStepCompletionPayload } from '@ee/common/forms';
import { IntakeEvictionFormService } from '../services/intake-eviction-form.service';
import {
  FieldDefinitionView,
  LayoutPageView,
  SubmissionParts,
  UNIT_TYPE_OPTION_KEY,
  buildIntakeForm,
  evaluateRules,
  toDefinitions,
  toPages,
  attorneyOnlyFieldKeys,
  withComputedTotals,
  withoutAttorneyOnly,
  toSubmissionParts,
  valuesForEvictionReasonRules,
  withSectionTargets,
  IntakeHostContext
} from '@ee/common/intake-layout';

/**
 * The v2 client wizard, wired to the same submission as v1.
 *
 * A separate component rather than a rewrite of `CreateEvictionComponent`, because the invariant for
 * this whole effort is that v1 keeps working *and stays editable* until the last firm is migrated. The
 * two are chosen between by `EvictionIntakeShellComponent`, so all three hosts — the public link, the
 * portal wizard and the PM send-to-attorney slide-in — get the switch without knowing about it.
 *
 * What it does not reimplement: the submission. The built `IntakeEviction` goes through the same
 * service call, and `submitHandler` is honoured, so the handoff path keeps working.
 */
@Component({
  selector: 'ee-eviction-wizard-v2',
  // Declared in `EvictionIntakeModule`, not standalone: it renders v1's own components, and those are
  // module-declared. A standalone component cannot import them directly.
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The address block three levels down reads who is filling the form, the v1 options and whether a
  // court may be chosen from this, so the attorney intake behaves as v1's property step did.
  providers: [IntakeHostContext],
  template: `
    @if (loading()) {
      <p class="wizard-v2__loading">Loading the form…</p>
    } @else if (submitted()) {
      <!--
        The firm's "What happens next", once the filing has gone. Not a step: shown before submit it
        reads as one more thing to fill in, and it describes what follows a submission that has not
        happened yet.
      -->
      @for (page of postSubmitPages(); track page.id) {
        <div class="wizard-v2__post-submit">
          <h1 class="wizard-v2__post-submit-title">{{ page.name }}</h1>
          @for (section of page.sections; track section.key) {
            <section class="ee__intake-card">
              @if (section.title) {
                <header class="ee__intake-card-header">
                  <h2 class="ee__intake-card-title">{{ section.title }}</h2>
                </header>
              }
              <ee-intake-layout-renderer
                [rows]="section.rows"
                [definitions]="definitions()"
                [preview]="true"></ee-intake-layout-renderer>
            </section>
          }
        </div>
      }
    } @else if (form) {
      <ee-intake-wizard
        [pages]="pages()"
        [form]="form"
        [definitions]="definitions()"
        [rules_]="rules()"
        [unitTypes]="unitTypes()"
        [fieldStyle]="fieldStyle()"
        [reviewStep]="reviewStep()"
        [plaintiffOptions]="plaintiffOptions()"
        [leadingStepName]="firmStep ? 'Case setup' : undefined"
        leadingStepNote="Who this is for, and how your firm will run it."
        [leadingStepValid]="firmStepValid()"
        [submitting]="submitting()"
        (submit)="save()">
        @if (firmStep) {
          <!--
            The firm's own step. None of it is a question for the client, and all of it decides what the
            rest of the case becomes: which client, which workflow, which attorney, and the workflow's
            own intake fields.

            Reusing v1's components rather than rebuilding them. They are driven by
            IntakeEvictionFormService, which already validates this exact group — and the client, the
            workflow and the step fields are not layout at all, so there is nothing here for the
            authored rows to describe.
          -->
          <div wizardLeadingStep class="wizard-v2__firm-step">
            <ee-workflow-form
              [cardStyle]="true"
              [preSelectedClientId]="selectedClientId"
              [cloneCase]="cloneCase"
              (addClient)="addClient.emit($event)"
              (workflowSelected)="onWorkflowSelected($event)"></ee-workflow-form>

            @if (selectedWorkflow() && formService.intakeStep) {
              <section class="ee__intake-card">
                <header class="ee__intake-card-header">
                  <h2 class="ee__intake-card-title">Intake fields</h2>
                  <!-- The workflow's name is data, so it reads as the lede rather than shouting in caps. -->
                  <p class="ee__intake-card-lede">{{ selectedWorkflow()?.name }}</p>
                </header>
                <ee-complete-workflow-step
                  [caseSettings]="caseSettings"
                  [nextStep]="formService.intakeStep"
                  [clientId]="$safeNavigationMigration(formService.workflowDetails?.get('client_details')?.value?.id)"
                  [userSuggestions]="formService.userSuggestions"
                  [tenants]="formService.tenants"
                  [planAllowsCaseTasks]="false"
                  [parentForm]="formService.splitForm"
                  [hideLabel]="true"
                  [forceVertical]="true" />
              </section>
            }
          </div>
        }
      </ee-intake-wizard>
    }
  `,
  styles: [
    `
      /*
       * The step's own stacking. ee__intake-card spaces one card off the card before it, but here the
       * workflow form's cards are inside a wrapper element, so the intake-fields card has no card
       * sibling to space off — the gap has to come from the container.
       */
      .wizard-v2__firm-step {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .wizard-v2__post-submit {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .wizard-v2__post-submit-title {
        margin: 0;
      }

      .wizard-v2__loading {
        padding: 32px;
        text-align: center;
        color: var(--color-on-surface-variant);
      }
    `
  ]
})
export class EvictionWizardV2Component implements OnInit, OnChanges, OnDestroy {
  /** Whose form to render. Absent for a signed-in client, whose attorney is already known. */
  @Input() attorneyId?: string;

  /** True on the public link, where page one creates the account. */
  @Input() forceIntakeForm = false;

  /** Property-linked PM mode: the server synthesizes the client, so page one is not asked. */
  @Input() skipClientStep = false;

  /** The handoff path submits through its own endpoint — create intake, link, freeze the case. */
  @Input() submitHandler?: (intake: IntakeEviction) => Observable<string | undefined>;

  @Input() loggedInOrg?: Organization;

  @Input() loggedInUser?: User;

  @Input() caseSettings?: CaseSettings;

  @Input() selectedClientId?: string;

  @Input() cloneCase?: Eviction;

  /**
   * The firm's workflows for the firm step's `ee-workflow-form`, which reads them from the shared
   * form service. v1 pushes them there in its own ngOnChanges; without this the v2 firm step listed none.
   */
  @Input() workflows: Workflow[] | null = null;

  @Output() evictionCreated = new EventEmitter<string | undefined>();

  @Output() addClient = new EventEmitter<boolean>();

  private readonly intakeFormService = inject(IntakeFormService);
  private readonly clientService = inject(ClientService);
  private readonly customFieldService = inject(CustomFieldService);
  /** Public so the template can read the group v1's own components are bound to. */
  protected readonly formService = inject(IntakeEvictionFormService);
  private readonly intakeEvictionService = inject(IntakeEvictionService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostContext = inject(IntakeHostContext);
  /** Set when this wizard initialized the firm step, so it clears only state it owns. */
  private initializedFirmStep = false;

  protected readonly loading = signal(true);
  protected readonly pages = signal<LayoutPageView[]>([]);
  protected readonly definitions = signal<Map<string, FieldDefinitionView>>(new Map());
  protected readonly rules = signal<LayoutRule[]>([]);
  protected readonly unitTypes = signal<string[]>([]);
  /** EE-367: the current client's plaintiff options. Null (no client yet) keeps a text field. */
  protected readonly plaintiffOptions = signal<PlaintiffOptions | null>(null);
  /** Bumps on each client change so a slow response cannot paint the previous client's options. */
  private plaintiffLoad = 0;
  /** True while a client's plaintiff options are loading; submitting waits for them (EE-367). */
  protected readonly plaintiffLoading = signal(false);
  /** The client whose options are loaded; a clone's plaintiff only carries over to its own client. */
  private plaintiffClientId: string | undefined;
  /** The authored "What happens next" pages, kept out of the steps and shown once the submit succeeds. */
  protected readonly postSubmitPages = signal<LayoutPageView[]>([]);
  protected readonly submitted = signal(false);
  /**
   * A submit is in flight. Set before subscribing and cleared only on error: after success the host
   * navigates away or the post-submit page replaces the wizard, and a second request would file twice.
   */
  protected readonly submitting = signal(false);

  protected form?: FormGroup;

  /**
   * Whether the firm is the one filling this in.
   *
   * An attorney creating a case on a client's behalf chooses the client, the workflow and the attorney,
   * and answers the workflow's intake fields. A client filling their own form is the client, has one
   * attorney, and has no business seeing any of it — and the public link has nobody signed in at all.
   */
  protected get firmStep(): boolean {
    return !this.forceIntakeForm && this.loggedInOrg?.type === AccountType.ATTORNEY;
  }

  protected readonly selectedWorkflow = signal<Workflow | undefined>(undefined);

  private intakeForm?: IntakeForm;

  /** The firm's choice from the builder. A form that never made one is CLASSIC — the look clients know. */
  protected readonly fieldStyle = signal<IntakeFieldStyle>('CLASSIC');

  /** The firm's Review & file setting. Off for a form that predates it, so its clients keep their flow. */
  protected readonly reviewStep = signal(false);

  ngOnInit(): void {
    this.hostContext.loggedInOrg.set(this.loggedInOrg ?? null);
    this.hostContext.loggedInUser.set(this.loggedInUser ?? null);
    this.hostContext.attorneyFilling.set(this.firmStep);
    this.hostContext.clientFilling.set(!this.firmStep);
    // v1 told the public link by the absence of a logged-in org; the shell passes the attorney as the org
    // here, so the fact is stated instead of inferred.
    this.hostContext.publicLink.set(this.forceIntakeForm || !this.loggedInOrg);
    this.hostContext.allowCourtSelection.set(this.firmStep);
    this.hostContext.jurisdiction.set(true);
    if (!this.forceIntakeForm && this.loggedInOrg?.type === AccountType.CLIENT) {
      // A client filing for itself: its own county rules and address fill, as v1's form service set.
      this.hostContext.selectedClient.set(this.loggedInOrg);
    }
    /*
     * The firm step is driven by `IntakeEvictionFormService`, so it has to be initialised even though
     * the authored pages do not use it. Awaited before the wizard renders: `ee-workflow-form` binds
     * straight to `workflowDetails`, and rendering it against an uninitialised service is a template
     * reading controls that do not exist yet.
     */
    if (this.firmStep) {
      // Subscribe before initialize: selectedClient$ is a Subject, and the first emit happens inside it.
      this.formService.selectedClient$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((client) => {
        this.hostContext.selectedClient.set(client ?? null);
        void this.loadAccountPlaintiffNames(client?.id, this.loggedInOrg?.id, client?.company_name);
      });
      this.initializedFirmStep = true;
      void this.formService.initialize(
        false,
        this.attorneyId ?? this.loggedInOrg?.id ?? '',
        this.loggedInUser ?? null,
        this.loggedInOrg ?? null,
        this.cloneCase ?? null
      );
      // initialize builds the form synchronously, so workflows that arrived first can be applied now.
      this.applyWorkflows();
    } else if (!this.forceIntakeForm && this.loggedInOrg?.type === AccountType.CLIENT) {
      void this.loadAccountPlaintiffNames(
        this.loggedInOrg.id,
        this.loggedInOrg.attorney?.id,
        this.loggedInOrg.company_name
      );
    }

    // ISS-151: the org-scoped endpoint is permitAll; `GET /api/intake-form/type/{type}` is gated on the
    // form *editor* permission and 403s every staff user without it. A logged-in org reads its own form
    // the same way a client reads the attorney's. Only a caller with no org at all falls back.
    const orgId = this.attorneyId ?? this.loggedInOrg?.id;
    const load = orgId
      ? this.intakeFormService.findByTypeAndOrganizationId(IntakeFormType.EVICTION, orgId)
      : this.intakeFormService.findByType(IntakeFormType.EVICTION);

    /*
     * The org catalog, when this caller is allowed to read it.
     *
     * `GET /api/organizations/custom-fields` is attorney + field-view, and a 403 toasts. The public
     * link and a client have no catalog; their definitions come from the form, which is why placing
     * a field also copies it onto the section. The catalog is what still defines a field that was
     * placed before that copy existed — rows only, nothing in `sections`.
     *
     * A failure is not swallowed: without the definitions that field has no CUSTOM origin, so its answer
     * is sent at the top level and the server drops it. The error state below is the safer outcome.
     */
    const catalog =
      this.loggedInOrg?.type === AccountType.ATTORNEY &&
      this.loggedInUser?.permissions?.includes(Permission.ADMIN_FIELDS_VIEW)
        ? this.customFieldService.getCustomCaseFields('case')
        : of([] as CustomField[]);

    forkJoin({ form: load, orgCustomFields: catalog })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ form, orgCustomFields }) => this.build(form, orgCustomFields),
        error: () => {
          this.loading.set(false);
          this.snackBar.open('We could not load the form. Please try again shortly.', 'Dismiss', { duration: 5000 });
        }
      });
  }

  /**
   * `IntakeEvictionFormService` is root-scoped and `initialize()` returns at once when already
   * initialized, so the next intake would reuse this one's client, workflow and grafted step answers.
   * v1 clears it the same way. The submit is deliberately not cancelled: it may already have reached
   * the server, and the host navigates away on success.
   */
  ngOnDestroy(): void {
    if (this.initializedFirmStep) {
      this.formService.clearForm();
    }
  }

  private build(form: IntakeForm, orgCustomFields: CustomField[] = []): void {
    this.intakeForm = form;
    this.hostContext.options.set((form.options as unknown as Record<string, IntakeOption>) ?? null);
    this.fieldStyle.set(form.field_style ?? 'CLASSIC');
    this.reviewStep.set(form.review_step ?? false);
    const definitions = new Map(toDefinitions(form, orgCustomFields).map((definition) => [definition.key, definition]));

    /*
     * Page one is dropped for a signed-in client, and for property-linked PM mode.
     *
     * It asks for an email, a phone and a company name to open an account — questions a client who is
     * already signed in has answered, and which the server synthesizes in PM mode. Asking them again
     * is how the same firm ends up in the database twice.
     */
    const all = toPages(form);
    const asked = this.firmStep
      ? all
      : withoutAttorneyOnly(all, form.options as unknown as Record<string, IntakeOption>, attorneyOnlyFieldKeys(form));
    const pages = asked.filter((page) => !page.post_submit && (this.forceIntakeForm || !page.public_only));

    this.pages.set(this.skipClientStep ? pages.filter((page) => !page.public_only) : pages);
    this.postSubmitPages.set(all.filter((page) => page.post_submit && page.sections.length));
    // Total Owed is typed or computed by what this filler's pages place beside it.
    const askedDefinitions = withComputedTotals(definitions, this.pages());
    this.definitions.set(askedDefinitions);
    this.rules.set(form.layout_rules ?? []);
    this.unitTypes.set(
      (form.options as unknown as Record<string, { options?: string[] }>)?.[UNIT_TYPE_OPTION_KEY]?.options ?? []
    );
    this.form = buildIntakeForm(this.pages(), askedDefinitions);
    this.applyPlaintiffPrefill();
    if (this.forceIntakeForm) {
      this.suggestTypedCompanyName();
    }
    this.loading.set(false);
  }

  /**
   * Public intake has no client account yet: suggest the company name typed on page one, as v1 does,
   * and follow it as it is edited. Suggestions only — the plaintiff stays free text and is not prefilled.
   */
  private suggestTypedCompanyName(): void {
    const company = this.form?.get('company_name');
    if (!company) {
      return;
    }
    const suggest = (name: unknown) =>
      this.plaintiffOptions.set(suggestedPlaintiffOptions([typeof name === 'string' ? name : null]));
    suggest(company.value);
    company.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(suggest);
  }

  /**
   * Plaintiff options for one client account (EE-367).
   *
   * Public intake has no account to load; it is offered the company name typed on page one instead (see
   * suggestTypedCompanyName). A response for a client the user has already left is dropped.
   */
  private async loadAccountPlaintiffNames(
    clientId: string | undefined,
    attorneyId: string | undefined,
    companyName: string | null | undefined
  ): Promise<void> {
    const request = ++this.plaintiffLoad;
    this.plaintiffOptions.set(null);
    this.plaintiffClientId = clientId;
    if (this.forceIntakeForm) {
      this.plaintiffLoading.set(false);
      return;
    }
    this.plaintiffLoading.set(true);
    const options = await this.clientService.getPlaintiffOptions(clientId, attorneyId, companyName);
    if (request !== this.plaintiffLoad) {
      return;
    }
    this.plaintiffOptions.set(options);
    this.applyPlaintiffPrefill();
    this.plaintiffLoading.set(false);
  }

  /** The client's default (or a cloned case's plaintiff), unless the user already chose a valid one. */
  private applyPlaintiffPrefill(): void {
    const control = this.form?.get('plaintiff');
    const options = this.plaintiffOptions();
    if (!control || !options) {
      return;
    }
    const next = resolvePlaintiffValue(options, {
      current: control.value,
      dirty: control.dirty,
      cloneName: cloneNameFor(this.cloneCase, this.plaintiffClientId)
    });
    if (next !== control.value) {
      control.setValue(next);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflows']) {
      this.applyWorkflows();
    }
  }

  private applyWorkflows(): void {
    // `null` is still loading; an empty list is an answer and clears the previous one.
    if (this.initializedFirmStep && this.workflows) {
      this.formService.updateWorkflow(this.workflows);
    }
  }

  protected onWorkflowSelected(workflow: Workflow): void {
    this.selectedWorkflow.set(workflow);
  }

  /**
   * The firm's step is answered when v1's whole split form is valid, as v1 checks it on save.
   *
   * Not just the workflow-selection group: `ee-complete-workflow-step` grafts the workflow's intake
   * fields onto `splitForm.step_completion_details`, and their required controls belong to this step.
   */
  protected firmStepValid(): boolean {
    return !this.firmStep || this.formService.splitForm?.valid === true;
  }

  protected save(): void {
    if (!this.form || !this.intakeForm || this.submitting()) {
      return;
    }

    // Until the client's plaintiff options arrive the field is unrestricted text; a restricted client
    // must not be able to submit an off-list plaintiff in that window (EE-367).
    if (this.plaintiffLoading()) {
      this.snackBar.open('Still loading the plaintiff options. Try again in a moment.', 'Dismiss', { duration: 4000 });
      return;
    }

    // Continue is gated on the same check, but nothing after the firm step re-validates it.
    if (!this.firmStepValid()) {
      this.formService.splitForm?.markAllAsTouched();
      this.snackBar.open('Case setup is incomplete. Go back to it to finish the intake fields.', 'Dismiss', {
        duration: 5000
      });
      return;
    }

    let parts: SubmissionParts;
    try {
      parts = toSubmissionParts(this.pages(), this.form, this.definitions(), {
        includeAccount: this.forceIntakeForm && !this.skipClientStep,
        // Evaluated at submit over the same values, and expanded the same way, as the wizard that
        // rendered the form. A hidden reason must not file the default it still holds (ISS-163).
        hidden: withSectionTargets(
          evaluateRules(
            this.rules(),
            valuesForEvictionReasonRules((this.form.getRawValue() ?? {}) as Record<string, unknown>)
          ),
          this.pages()
        ).hidden
      });
    } catch (e) {
      // A document still uploading, or one whose upload failed: nothing is sent, and the client is told which.
      if (e instanceof FileStillUploadingError || e instanceof FileUploadFailedError) {
        this.snackBar.open(e.message, 'Dismiss', { duration: 5000 });
        return;
      }
      throw e;
    }

    /*
     * The firm's answers, where there is a firm step.
     *
     * `client_details` comes from the *selected client* rather than from page one — an attorney is
     * filing on someone's behalf, and page one is not even asked. Same for the workflow, the assigned
     * attorney and the step-completion fields: they are the firm's decisions about the case, and none
     * of them is expressible as a placement on the client's form.
     */
    const firm = this.firmStep ? (this.formService.workflowDetails?.value ?? {}) : {};
    const split = this.firmStep ? (this.formService.splitForm?.value ?? {}) : {};

    const intake = {
      type: this.attorneyId ? IntakeEvictionType.INTAKE_FORM : IntakeEvictionType.EXISTING_CLIENT,
      attorney_id: firm.attorney_id ?? this.attorneyId,
      intake_form_id: this.intakeForm.id,
      selected_workflow_id: firm.workflow_id,
      selected_attorney_user_id: firm.selected_attorney_user_id,
      // The grafted step form's raw value, so the wire conversions v1 applies are applied here too.
      step_completion_details: ToCreateStepCompletionPayload(split.step_completion_details),
      note: split.note,
      eviction_details: parts.evictionDetails,
      user_details: parts.userDetails ?? firm.user_details,
      client_details: parts.clientDetails ?? firm.client_details,
      // Staged uploads, as v1's documents step sends them; the legacy per-type map is still read by a
      // server that predates `uploads`.
      uploads: parts.uploads,
      upload_ids: Object.fromEntries(parts.uploads.map((upload) => [upload.type, upload.upload_id]))
    } as unknown as IntakeEviction;

    const submit$ = this.submitHandler
      ? this.submitHandler(intake)
      : this.intakeEvictionService.saveIntakeEvictionWithUploads(intake.type, intake);

    this.submitting.set(true);
    submit$.subscribe({
      next: (caseId) => {
        // Only a page with something on it replaces the wizard; otherwise the host's own confirmation stands.
        this.submitted.set(this.postSubmitPages().length > 0);
        this.evictionCreated.emit(caseId);
      },
      error: (error: HttpErrorResponse) => {
        // `BadRequestAlertException` puts the descriptive message in `title` and the short entity name
        // in `detail`; the title is the one worth showing a client.
        const message = error.error?.title || 'An error occurred submitting your request. Please try again shortly.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
        this.submitting.set(false);
      }
    });
  }
}
